import { getFontCss, styleConstants } from '@paywithextend/components'
import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectFilteredPeopleMembers } from 'src/app/PeopleMembers/redux/peopleMembers.selectors'
import AppName from 'src/components/AppName/AppName'
import Box from 'src/components/Box/Box'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
  userEmailOption,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { OrganizationRoles } from 'src/enums'
import { useFlyoutDispatch } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { UserAssociation, UserListModel } from 'src/models/user.models'
import {
  getOwnUser,
  getRecentUsers,
} from 'src/redux/actions/data/users.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserEmailMap } from 'src/redux/selectors/data/users.selectors'
import { FormProps } from 'src/types'
import { css } from 'styled-components'

interface UserInputBoxProps {
  className?: string
  id?: string
  userEmailProps: FormProps<string[]>
  isMulti?: boolean
  userFieldLabel: React.ReactNode
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  userInfiniteScrollProps?: UserDropdownInfiniteScrollProps
  suggestedProps?: {
    showSuggested: boolean | undefined
    showOwnUserInSuggested?: boolean
  }
  filterUserbyAssocationTypes?: UserAssociation[]
  getRecipientWarningMessage?: (
    hasSelectedUsersNotInOrg: boolean,
  ) => React.ReactNode
  unableToAddNewUser?: boolean
  helpText?: React.ReactElement
  tooltip?: React.ReactElement
  displayPills?: boolean
  preventGuests?: boolean
  selectedUsers?: UserListModel[]
  fetchSuggestedUsers?: boolean
}

const UserInputBox: React.FC<UserInputBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const locale = useSelector(selectNullSafeLocaleFromState)
  const userEmailMap = useSelector(selectUserEmailMap)
  const user = useCurrentUser()

  const memberListModels = useSelector(selectFilteredPeopleMembers)

  const memberEmailMap = useMemo(() => {
    return Object.values(memberListModels).reduce(
      (acc: Record<string, UserListModel>, curr) => ({
        ...acc,
        [curr.email]: curr,
      }),
      {},
    )
  }, [memberListModels])

  const selectedUserEmailMap = useMemo(() => {
    return (props.selectedUsers ?? []).reduce(
      (acc: Record<string, UserListModel>, curr) => ({
        ...acc,
        [curr.email]: curr,
      }),
      {},
    )
  }, [props.selectedUsers])

  const optionUserEmailMap = useMemo(() => {
    return [...props.userOptions, ...(props.recentUserOptions ?? [])].reduce(
      (acc: Record<string, UserListModel>, option) => {
        const optionUser = option.details?.user
        return optionUser ? { ...acc, [optionUser.email]: optionUser } : acc
      },
      {},
    )
  }, [props.recentUserOptions, props.userOptions])

  const emailMap = useMemo(() => {
    return {
      ...selectedUserEmailMap,
      ...userEmailMap,
      ...memberEmailMap,
      ...optionUserEmailMap,
    }
  }, [selectedUserEmailMap, userEmailMap, memberEmailMap, optionUserEmailMap])

  const addDeleteVal = (newSet: Set<string>, val: string): void => {
    if (newSet.has(val)) {
      newSet.delete(val)
    } else {
      newSet.add(val)
    }
  }

  const selectedOptions: UserOption[] | undefined = useMemo(
    () =>
      props.userEmailProps.value !== undefined
        ? props.userEmailProps.value
            .filter((value) => emailMap[value] !== undefined)
            .map((value) => userEmailOption(emailMap[value]))
        : undefined,
    [emailMap, props.userEmailProps.value],
  )

  const userEmailSet =
    props.userEmailProps.value !== undefined
      ? new Set(props.userEmailProps.value)
      : new Set<string>()

  const dispatch = useFlyoutDispatch()

  useEffect((): void => {
    if (
      props.fetchSuggestedUsers !== false &&
      props.suggestedProps?.showSuggested
    ) {
      if (props.suggestedProps.showOwnUserInSuggested) {
        dispatch(getOwnUser())
      }
      if (props.filterUserbyAssocationTypes) {
        if (
          props.filterUserbyAssocationTypes.includes(
            UserAssociation.virtualCardTo,
          )
        ) {
          dispatch(getRecentUsers(UserAssociation.virtualCardTo))
        }
        if (
          props.filterUserbyAssocationTypes.includes(
            UserAssociation.virtualCardFrom,
          )
        ) {
          dispatch(getRecentUsers(UserAssociation.virtualCardFrom))
        }
        if (
          props.filterUserbyAssocationTypes.includes(
            UserAssociation.delegateCreditCardTo,
          )
        ) {
          dispatch(getRecentUsers(UserAssociation.delegateCreditCardTo))
        }
      } else {
        dispatch(getRecentUsers(UserAssociation.virtualCardTo))
        dispatch(getRecentUsers(UserAssociation.invitationTo))
      }
    }
  }, [])

  const selectedEmails = useMemo(
    () => selectedOptions?.map((selectedOption) => selectedOption.value),
    [selectedOptions],
  )
  const selectedUsers = useMemo(
    () =>
      Object.values(emailMap).filter((user) =>
        selectedEmails?.includes(user.email),
      ),
    [selectedEmails, emailMap],
  )
  const newEmails = useMemo(
    () =>
      Array.from(userEmailSet)?.filter(
        (email) => Object.keys(emailMap).indexOf(email) < 0,
      ),
    [userEmailSet, emailMap],
  )
  const selectedUsersNotInOrg = useMemo(
    () =>
      selectedUsers
        ?.filter(
          (selectedUser) =>
            (selectedUser?.organizationId ?? selectedUser?.organization?.id) !==
            (user?.organizationId ?? user?.organization?.id),
        )
        .map((user) => user.email)
        .concat(newEmails),
    [selectedUsers, user],
  )

  const handleEmailSelection = (val: string): void => {
    const newSet = new Set(props.userEmailProps.value)
    addDeleteVal(newSet, val)
    props.isMulti
      ? props.userEmailProps.onChange(Array.from(newSet))
      : props.userEmailProps.onChange([val])
  }

  const handleMultiEmailSelection = (val: string[]): void => {
    const newSet = new Set(props.userEmailProps.value)
    val.map((val) => {
      addDeleteVal(newSet, val)
    })
    props.userEmailProps.onChange(Array.from(newSet))
  }

  return (
    <Box
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      <UserDropdown
        css={css`
          &:nth-child(n + 2) {
            margin-top: 24px;
          }
          ${getFontCss(
            16,
            styleConstants.colors.gray_darkest,
            styleConstants.fontFamilies.regular,
          )};
          z-index: 3;
        `}
        fieldLabel={props.userFieldLabel}
        selectedOptions={selectedOptions}
        optionGroups={
          props.userOptions.length > 0
            ? [
                {
                  label:
                    user?.hasOrgAdminPermissions ||
                    user?.organizationRole === OrganizationRoles.cardManager
                      ? translate('${appName} Contacts', {
                          appName: <AppName />,
                        })
                      : translate('${appName} Org Members', {
                          appName: <AppName />,
                        }),
                  options: props.userOptions,
                },
              ]
            : []
        }
        recentOptionGroups={
          props.recentUserOptions && props.recentUserOptions.length > 0
            ? [
                {
                  label: translate('Suggested', {
                    appName: <AppName />,
                  }),
                  options: props.recentUserOptions,
                  hideGroup: false,
                },
              ]
            : []
        }
        isCreatable={!props.unableToAddNewUser}
        isMulti={!!props.isMulti}
        showSuggested={!!props.suggestedProps?.showSuggested}
        isStatic={props.userEmailProps.isStatic}
        errors={props.userEmailProps.errors}
        chooseOption={handleEmailSelection}
        chooseMultiOption={handleMultiEmailSelection}
        selectedValues={userEmailSet}
        placeholder={
          props.isMulti
            ? translateSingleString(locale, 'Enter one or more emails')
            : translateSingleString(locale, 'Type or select from list')
        } // change singlular text?
        newOptionLabel={translate('New Contact')}
        infiniteScrollProps={props.userInfiniteScrollProps}
        tooltip={props.tooltip}
        displayPills={props.displayPills}
        id={props.id}
        preventGuests={props.preventGuests}
      />
      {props?.helpText}
      {props.getRecipientWarningMessage &&
        props.getRecipientWarningMessage(selectedUsersNotInOrg.length > 0)}
    </Box>
  )
}

export default UserInputBox
